import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/basedata/apiCall';
import { clearStoredInviteToken, getStoredInviteToken } from '@/helpers/inviteToken';
import { navigateToError } from '@/helpers/navigateToError';
import { getOnboardingGuest, setOnboardingGuest } from '@/helpers/onboardingGuest';
import { getOnboardingSessionId } from '@/helpers/onboardingSession';
import type { InviteRole, LinkResponse } from '@/types/guestOnboarding';

const INVITE_PATH = '/invite';

/**
 * `confirm`: o link é pessoal, então a tela só confirma QUEM é ("Você é
 * Fulano?"). `group`: o titular DESTE aparelho já tem biometria — banner de
 * "facial pronta" + cadastro dos acompanhantes na MESMA tela.
 */
export type InviteScreen = 'loading' | 'invalid' | 'confirm' | 'group';

/**
 * Como a tela chama quem abriu o link:
 *  - `named`: o convite tem nome ("Você é Fulano?");
 *  - `guestOf`: vaga/convidado sem nome — usa o titular e, se nem ele tiver
 *    nome, o evento ("Você é um convidado de Ciclano?");
 *  - `generic`: não sobrou nenhum nome para citar.
 */
export type InviteConfirmation =
  | { kind: 'named'; name: string }
  | { kind: 'guestOf'; host: string }
  | { kind: 'generic' };

export type UseInviteResult = {
  screen: InviteScreen;
  invite: LinkResponse['invite'] | null;
  /** Papel do CONVITE — a tela mostra, o usuário não escolhe. */
  role: InviteRole | null;
  confirmation: InviteConfirmation;
  /** Nome exibido no banner do grupo — do convidado deste aparelho ou do convite. */
  titularName: string;
  isSubmitting: boolean;
  confirm: () => Promise<void>;
  /** "Não sou eu": cai na identificação por documento. */
  reject: () => void;
};

/** Perfil do convidado amarrado pelo link — pré-preenche o cadastro adiante. */
function storeGuestFromLink(response: LinkResponse): void {
  const guest = response.guest;
  setOnboardingGuest({
    fullName: guest.fullName ?? '',
    email: guest.email ?? null,
    phone: guest.phone ?? null,
    birthDate: guest.birthDate ?? null,
    nationality: guest.nationality ?? null,
    documentDigits: '',
    onboardingStep: guest.onboardingStep ?? 0,
    companions: response.invite.companions,
    role: response.role,
    alreadyOnboarded: guest.alreadyOnboarded === true,
    enrolledAt: guest.enrolledAt ?? null,
  });
}

/** Este aparelho já é o titular com biometria pronta (funil concluído aqui). */
function isEnrolledTitular(guest: ReturnType<typeof getOnboardingGuest>): boolean {
  return guest !== null && guest.alreadyOnboarded && guest.role !== 'companion';
}

/** Nome a citar na confirmação, na ordem em que a pessoa reconhece. */
function confirmationFrom(response: LinkResponse): InviteConfirmation {
  if (response.guestName.trim()) return { kind: 'named', name: response.guestName.trim() };
  const host = response.titularName.trim() || response.eventName.trim();
  return host ? { kind: 'guestOf', host } : { kind: 'generic' };
}

/**
 * Tela ÚNICA do convite/grupo (`/companions` não existe mais como rota).
 *
 * O convite chega como LINK PESSOAL: o token identifica uma pessoa, então não
 * há CPF a pedir (o organizador nem sempre tem) nem papel a perguntar — o papel
 * é do convite. A tela só confirma o nome e segue para a selfie e o cadastro.
 *
 * Perguntar o papel era um buraco: o link do titular era compartilhado e quem
 * abrisse podia se declarar titular. Agora cada acompanhante tem o link dele.
 */
export function useInvite(): UseInviteResult {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<InviteScreen>('loading');
  const [invite, setInvite] = useState<LinkResponse['invite'] | null>(null);
  const [role, setRole] = useState<InviteRole | null>(null);
  const [confirmation, setConfirmation] = useState<InviteConfirmation>({ kind: 'generic' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resolved = useRef(false);

  const token = getStoredInviteToken();
  const sessionId = getOnboardingSessionId();
  const guest = getOnboardingGuest();
  const [titularName, setTitularName] = useState(guest?.fullName ?? '');

  useEffect(() => {
    if (resolved.current) return;
    resolved.current = true;

    if (sessionId === null) {
      navigate('/terms', { replace: true });
      return;
    }

    // Titular deste aparelho com biometria pronta: abre o grupo direto, sem
    // gastar chamada de link nem repetir a confirmação que ele já deu.
    if (isEnrolledTitular(guest)) {
      setScreen('group');
      return;
    }

    // Sem token e sem biometria não há convite nem grupo para mostrar — a
    // pessoa precisa se identificar pelo documento antes.
    if (token === null) {
      navigate('/identify', { replace: true });
      return;
    }

    let cancelled = false;
    void api.onboarding
      .link({ token, sessionId })
      .then((response) => {
        if (cancelled) return;
        setInvite(response.invite);
        setRole(response.role);
        setConfirmation(confirmationFrom(response));
        setTitularName(response.guestName || response.invite.titularName);
        setScreen('confirm');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 404) {
          setScreen('invalid');
          return;
        }
        navigateToError(navigate, {
          error,
          originPath: INVITE_PATH,
          ...(error instanceof ApiError ? { errorCode: String(error.status) } : {}),
        });
      });
    return () => {
      cancelled = true;
    };
    // token/sessionId vêm do storage — estáveis durante a vida da página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = useCallback(async (): Promise<void> => {
    if (token === null || sessionId === null) return;
    setIsSubmitting(true);
    try {
      const response = await api.onboarding.link({ token, sessionId, confirm: true });
      storeGuestFromLink(response);

      // Quem já tem biometria não repete selfie/cadastro: cada passagem custa
      // uma sessão de liveness + uma indexação de face.
      if (response.guest.alreadyOnboarded === true) {
        if (response.role === 'titular') {
          setInvite(response.invite);
          setTitularName(response.guest.fullName || response.invite.titularName);
          setScreen('group');
          return;
        }
        navigate('/success', { state: { sessionId } });
        return;
      }

      navigate('/selfie', { state: { sessionId } });
    } catch (error) {
      // Link morto/rede: a ErrorPage explica e permite voltar ao convite.
      navigateToError(navigate, {
        error,
        originPath: INVITE_PATH,
        ...(error instanceof ApiError ? { errorCode: String(error.status) } : {}),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate, sessionId, token]);

  // "Não sou eu": o link não é desta pessoa. A identificação por documento
  // continua existindo para quem chega sem link (ou com o link de outro).
  const reject = useCallback((): void => {
    // Sem esquecer o token, o /identify devolve para cá (há token guardado) e
    // a pessoa fica presa no "Você é Fulano?" de outra pessoa.
    clearStoredInviteToken();
    navigate('/identify');
  }, [navigate]);

  return { screen, invite, role, confirmation, titularName, isSubmitting, confirm, reject };
}
